import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useEffect, useMemo } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import {
  GoogleApiDisclaimer,
  GoogleCalendarAuthButton,
} from '@/components/auth';
import { useAddSource } from '@/features/sources/hooks/useAddSource';
import type { LaunchOption } from '@/hooks/useLauncher';
import { useIntegrations } from '@/pages/Schedule/hooks/useIntegrations';
import { IntegrationType } from '@/types/gql.generated';
import {
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Select,
  Spinner,
  Text,
  VStack,
} from '@/ui';
import { getErrorMessage } from '@/utils/errors';
import {
  addCalendarSchema,
  type AddCalendarFormValues,
} from './addCalendar.schema';

type Props = {
  /** Called with the new source's feed id after it is successfully added. */
  onAdded: (feedId: string) => void;
  /**
   * Optional launch action appended to the Google OAuth redirect. The modal host
   * (FeedModal, not URL-driven) uses it to re-open the modal into the add view after
   * the round trip. The URL-driven Sources panel already restores its view from the
   * preserved URL, so it uses it only to trigger a "connected" confirmation toast.
   */
  launchOnRedirect?: LaunchOption;
};

// Presentational, container-agnostic Google Calendar connect form. Hosts supply
// their own outer padding (modal body, panel content area).
export const GoogleCalendarForm = ({ onAdded, launchOnRedirect }: Props) => {
  const { data, isLoading, isError } = useIntegrations();
  const { addSource, isPending, error } = useAddSource();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AddCalendarFormValues>({
    defaultValues: { integrationId: '', calendarUri: '' },
    resolver: standardSchemaResolver(addCalendarSchema),
  });

  const integrations = useMemo(
    () => (data ?? []).filter((i) => i.type === IntegrationType.GoogleCalendar),
    [data]
  );

  const selectedIntegrationId = watch('integrationId');

  useEffect(() => {
    if (integrations.length && !selectedIntegrationId) {
      setValue('integrationId', integrations[0].id);
    }
  }, [integrations, selectedIntegrationId, setValue]);

  const calendars = useMemo(() => {
    const integration = integrations.find(
      (i) => i.id === selectedIntegrationId
    );
    return integration?.calendars ?? [];
  }, [integrations, selectedIntegrationId]);

  if (isLoading) {
    return (
      <Flex align="center" gap="3">
        <Spinner size="sm" />
        <Text>Loading your calendars…</Text>
      </Flex>
    );
  }

  if (isError) {
    return <Text>Couldn&apos;t load your calendars. Please try again.</Text>;
  }

  const connectPrompt = (
    <VStack spacing="6">
      <GoogleCalendarAuthButton launchOnRedirect={launchOnRedirect} />
      <GoogleApiDisclaimer textAlign="center" />
    </VStack>
  );

  if (!integrations.length) {
    return connectPrompt;
  }

  const onSubmit: SubmitHandler<AddCalendarFormValues> = ({ calendarUri }) => {
    addSource(calendarUri, {
      onSuccess: (data) => {
        toast.notify('Calendar added');
        onAdded(data.addSourceToExperience.id);
      },
    });
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gap="5">
        {error && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>{getErrorMessage(error)}</AlertTitle>
          </Alert>
        )}

        <FormControl isInvalid={!!errors.integrationId}>
          <FormLabel variant="uppercase">Account</FormLabel>
          <Select
            {...register('integrationId', {
              onChange: () => setValue('calendarUri', ''),
            })}
          >
            {integrations.map((integration) => (
              <option key={integration.id} value={integration.id}>
                {integration.name}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.integrationId?.message}</FormErrorMessage>
        </FormControl>

        <FormControl
          isDisabled={!selectedIntegrationId}
          isInvalid={!!errors.calendarUri}
        >
          <FormLabel variant="uppercase">Calendar</FormLabel>
          <Select {...register('calendarUri')}>
            <option value="">Select a calendar…</option>
            {calendars.map((calendar) => (
              <option key={calendar.uri} value={calendar.uri}>
                {calendar.name}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.calendarUri?.message}</FormErrorMessage>
        </FormControl>

        <Button alignSelf="flex-start" isLoading={isPending} type="submit">
          <Icon icon="Plus" mr="1.5" />
          Add calendar
        </Button>

        <Box borderTopWidth="1px" pt="5">
          <Text color="gray.500" fontSize="sm" mb="3">
            Or connect another Google account
          </Text>
          {connectPrompt}
        </Box>
      </Flex>
    </form>
  );
};
